/**
 * Motor de imposición de pliegos.
 *
 * Todas las medidas en centímetros (cm).
 * Cada opción retorna:
 *  - id, label
 *  - items_per_sheet: piezas que entran por pliego (1 lado)
 *  - pieces: array de rectángulos { x, y, w, h, rotated } para dibujar
 *  - usage: % de aprovechamiento del área imprimible
 *  - sheets_needed: cantidad de pliegos para la tirada (según doubleSided)
 */

// Cuántas piezas de tamaño (w x h) entran en un área de (aw x ah) con separación s
function fitGrid(areaW, areaH, itemW, itemH, sep) {
  if (itemW <= 0 || itemH <= 0 || areaW < itemW || areaH < itemH) {
    return { cols: 0, rows: 0, total: 0 };
  }
  // n items caben en L si: n*item + (n-1)*sep <= L  ->  n <= (L + sep) / (item + sep)
  const cols = Math.max(0, Math.floor((areaW + sep) / (itemW + sep)));
  const rows = Math.max(0, Math.floor((areaH + sep) / (itemH + sep)));
  return { cols, rows, total: cols * rows };
}

// Construye array de piezas centrado dentro del área [offsetX, offsetY] con tamaño (areaW x areaH)
function buildGridPieces(cols, rows, itemW, itemH, sep, offsetX, offsetY, rotated) {
  const pieces = [];
  const gridW = cols * itemW + Math.max(0, cols - 1) * sep;
  const gridH = rows * itemH + Math.max(0, rows - 1) * sep;
  const startX = offsetX; // pegado al margen izquierdo
  const startY = offsetY; // pegado arriba del área
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      pieces.push({
        x: startX + c * (itemW + sep),
        y: startY + r * (itemH + sep),
        w: itemW,
        h: itemH,
        rotated: Boolean(rotated),
      });
    }
  }
  return pieces;
}

/**
 * Calcula las opciones de imposición.
 *
 * @param {Object} params
 * @param {number} params.sheetWidth  - Ancho del pliego (cm)
 * @param {number} params.sheetHeight - Alto del pliego (cm)
 * @param {number} params.gripperTop  - Pinza superior (cm)
 * @param {number} params.gripperBottom - Pinza inferior (cm)
 * @param {number} params.marginLeft - Margen izquierdo adicional (cm)
 * @param {number} params.marginRight - Margen derecho adicional (cm)
 * @param {number} params.itemWidth  - Ancho de la pieza a imprimir (cm)
 * @param {number} params.itemHeight - Alto de la pieza a imprimir (cm)
 * @param {number} params.bleed - Sangrado alrededor de cada pieza (cm)
 * @param {number} params.separation - Separación entre piezas (cm)
 * @param {boolean} params.allowRotation - Permite considerar orientación rotada
 * @param {boolean} params.doubleSided - Si es doble faz
 * @param {number} params.quantity - Cantidad de piezas a imprimir
 * @returns {Array} lista de opciones ordenadas por eficiencia
 */
export function calculateImposition(params) {
  const {
    sheetWidth = 52,
    sheetHeight = 74,
    gripperTop = 2,
    gripperBottom = 0,
    marginLeft = 0,
    marginRight = 0,
    itemWidth = 0,
    itemHeight = 0,
    bleed = 0,
    separation = 0,
    allowRotation = true,
    doubleSided = false,
    quantity = 1,
  } = params;

  const effW = itemWidth + 2 * bleed;
  const effH = itemHeight + 2 * bleed;

  const areaW = sheetWidth - marginLeft - marginRight;
  const areaH = sheetHeight - gripperTop - gripperBottom;
  const areaX = marginLeft;
  const areaY = gripperTop;

  const printableArea = areaW * areaH;

  const options = [];

  // ===== 1) Todos vertical (orientación original) =====
  const gV = fitGrid(areaW, areaH, effW, effH, separation);
  if (gV.total > 0) {
    const pieces = buildGridPieces(gV.cols, gV.rows, effW, effH, separation, areaX, areaY, false);
    options.push({
      id: 'all-vertical',
      label: 'Todos en orientación original',
      items_per_sheet: gV.total,
      cols: gV.cols,
      rows: gV.rows,
      pieces,
      usage: Math.min(100, (gV.total * effW * effH) / printableArea * 100),
      config: { itemWidth, itemHeight, effW, effH },
    });
  }

  // ===== 2) Todos rotados 90° =====
  if (allowRotation && itemWidth !== itemHeight) {
    const gH = fitGrid(areaW, areaH, effH, effW, separation);
    if (gH.total > 0) {
      const pieces = buildGridPieces(gH.cols, gH.rows, effH, effW, separation, areaX, areaY, true);
      options.push({
        id: 'all-rotated',
        label: 'Todos rotados 90°',
        items_per_sheet: gH.total,
        cols: gH.cols,
        rows: gH.rows,
        pieces,
        usage: Math.min(100, (gH.total * effW * effH) / printableArea * 100),
        config: { itemWidth, itemHeight, effW, effH },
      });
    }
  }

  // ===== 3) Mixta: dividir horizontalmente (arriba rotado, abajo original) =====
  if (allowRotation && itemWidth !== itemHeight) {
    const bestMixed = findBestMixedSplit(areaW, areaH, effW, effH, separation, areaX, areaY);
    if (bestMixed && bestMixed.total > 0) {
      // Solo agregar si es distinto/mejor a las opciones puras
      const alreadyBetter = options.some(o => o.items_per_sheet >= bestMixed.total);
      if (!alreadyBetter || bestMixed.total > 0) {
        options.push({
          id: 'mixed',
          label: 'Mixta (aprovechamiento óptimo)',
          items_per_sheet: bestMixed.total,
          cols: null,
          rows: null,
          pieces: bestMixed.pieces,
          usage: Math.min(100, (bestMixed.total * effW * effH) / printableArea * 100),
          config: { itemWidth, itemHeight, effW, effH, split: bestMixed.split },
        });
      }
    }
  }

  // Filtrar duplicados exactos por items_per_sheet + orientación
  const seen = new Set();
  const unique = options.filter(o => {
    const key = `${o.id}-${o.items_per_sheet}-${o.rows || 0}-${o.cols || 0}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // Calcular sheets_needed para cada opción
  unique.forEach(o => {
    const perSheet = o.items_per_sheet;
    if (perSheet <= 0) {
      o.sheets_needed = 0;
      return;
    }
    // En doble faz, cada pliego permite imprimir 1 pieza por cada slot (frente + dorso del mismo diseño = 1 pieza)
    // Para doble faz distinto (frente y dorso diferentes) se necesita 1 pliego por pieza igual
    // Simplificamos: doble faz no divide la cantidad, solo indica que ambos lados se imprimen
    o.sheets_needed = Math.ceil(quantity / perSheet);
  });

  // Ordenar: más items por pliego primero
  unique.sort((a, b) => b.items_per_sheet - a.items_per_sheet);

  return unique;
}

// Busca el mejor split horizontal probando varias alturas de zona superior
function findBestMixedSplit(areaW, areaH, effW, effH, sep, areaX, areaY) {
  let best = null;

  // Probar splits en múltiplos de (effW + sep) — la altura superior debe ser suficiente
  // para contener n filas de piezas rotadas (altura de fila = effW cuando rotan)
  const maxRotatedRows = Math.floor((areaH + sep) / (effW + sep));

  for (let rotRows = 0; rotRows <= maxRotatedRows; rotRows++) {
    // Altura ocupada por la zona rotada
    const rotBlockH = rotRows > 0 ? rotRows * effW + (rotRows - 1) * sep : 0;
    // Espacio para la zona normal (con un gap de separación si ambas zonas tienen contenido)
    const remainingH = areaH - rotBlockH - (rotRows > 0 ? sep : 0);

    // Cuántas columnas de piezas rotadas
    const rotCols = rotRows > 0 ? Math.floor((areaW + sep) / (effH + sep)) : 0;
    const topTotal = rotCols * rotRows;

    // Zona inferior con orientación original
    let botRows = 0;
    let botCols = 0;
    let botTotal = 0;
    if (remainingH >= effH) {
      botCols = Math.floor((areaW + sep) / (effW + sep));
      botRows = Math.floor((remainingH + sep) / (effH + sep));
      botTotal = botCols * botRows;
    }

    const total = topTotal + botTotal;
    if (!best || total > best.total) {
      const pieces = [];
      // Piezas rotadas (arriba)
      if (topTotal > 0) {
        pieces.push(...buildGridPieces(rotCols, rotRows, effH, effW, sep, areaX, areaY, true));
      }
      // Piezas normales (abajo)
      if (botTotal > 0) {
        const botY = areaY + rotBlockH + (rotRows > 0 ? sep : 0);
        pieces.push(...buildGridPieces(botCols, botRows, effW, effH, sep, areaX, botY, false));
      }
      best = {
        total,
        pieces,
        split: { rotRows, rotCols, botRows, botCols },
      };
    }
  }

  return best;
}

/**
 * Serializa una opción de imposición a un objeto simple para guardar en el presupuesto snapshot.
 */
export function serializeImpositionChoice(option, params) {
  if (!option) return null;
  return {
    id: option.id,
    label: option.label,
    items_per_sheet: option.items_per_sheet,
    sheets_needed: option.sheets_needed,
    usage: option.usage,
    pieces: option.pieces,
    params,
  };
}
